// src/pages/auth/ResetPassword.tsx
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createResource,getResources,deleteResource, updateResource} from '../../router/api';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<'password' | 'confirmation' | null>(null);

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  // Redirection si token ou email absent
  useEffect(() => {
    if (!token || !email) {
      setError('Lien de réinitialisation invalide ou expiré.');
    }
  }, [token, email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (password !== passwordConfirmation) {
      setError('Les mots de passe ne correspondent pas.');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      setLoading(false);
      return;
    }

    try {
      const response = await createResource(`reset-password`, {
        email,
        token,
        password,
        password_confirmation: passwordConfirmation,
      });

      if (response.data.success) {
        setSuccess(response.data.message || 'Mot de passe réinitialisé avec succès !');
        // Optionnel : redirection auto après 3 secondes
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err: any) {
      const serverError =
        err.response?.data?.message ||
        err.response?.data?.errors?.password?.[0] ||
        err.response?.data?.errors?.email?.[0] ||
        'Erreur lors de la réinitialisation. Le lien a peut-être expiré.';
      setError(serverError);
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50/30 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <i className="ri-error-warning-line text-8xl text-red-400 mb-6"></i>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Lien invalide</h2>
          <p className="text-gray-600 mb-8">
            Ce lien de réinitialisation est invalide ou a expiré. Veuillez recommencer la procédure.
          </p>
          <button
            onClick={() => navigate('/forgot-password')}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition"
          >
            Nouvelle demande de réinitialisation
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50/30 flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md transition-all duration-300">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block p-4 bg-blue-600 rounded-2xl shadow-xl transform transition-transform hover:scale-105 duration-300">
            <i className="ri-building-2-fill text-white text-4xl"></i>
          </div>
          <h1 className="mt-6 text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            MAI MAB SERVICES
          </h1>
          <p className="mt-2 text-gray-600 font-medium">Réinitialisation du mot de passe</p>
        </div>

        {/* Card */}
        <div
          className={`bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl p-6 sm:p-8 border border-gray-100/50
            transition-all duration-500 transform
            ${focusedField ? 'scale-[1.015] shadow-2xl' : 'scale-100 shadow-xl'}`}
        >
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
              <i className="ri-lock-password-line text-blue-600 text-3xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Choisissez un nouveau mot de passe</h2>
            <p className="mt-3 text-gray-600 text-sm">
              Votre nouveau mot de passe doit contenir au moins 8 caractères.
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-3">
              <i className="ri-error-warning-line text-red-600 text-xl mt-0.5" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start space-x-3">
              <i className="ri-check-double-line text-green-600 text-xl mt-0.5" />
              <span className="text-sm text-green-700">
                {success} Redirection vers la connexion dans quelques instants...
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nouveau mot de passe */}
            <div className="relative">
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                className={`peer w-full px-4 pt-6 pb-2 border border-gray-300 rounded-xl 
                  focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30
                  transition-all duration-300 bg-white/60
                  ${password ? 'border-blue-400' : ''}`}
                placeholder=" "
                required
                minLength={8}
              />
              <label
                htmlFor="password"
                className={`absolute left-4 text-sm text-gray-500 transition-all duration-300 pointer-events-none
                  ${password || focusedField === 'password' 
                    ? '-top-0.5 text-xs text-blue-600 font-medium' 
                    : 'top-4 text-base text-gray-500'}`}
              >
                Nouveau mot de passe
              </label>
            </div>

            {/* Confirmation */}
            <div className="relative">
              <input
                type="password"
                id="passwordConfirmation"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                onFocus={() => setFocusedField('confirmation')}
                onBlur={() => setFocusedField(null)}
                className={`peer w-full px-4 pt-6 pb-2 border border-gray-300 rounded-xl 
                  focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30
                  transition-all duration-300 bg-white/60
                  ${passwordConfirmation ? 'border-blue-400' : ''}`}
                placeholder=" "
                required
              />
              <label
                htmlFor="passwordConfirmation"
                className={`absolute left-4 text-sm text-gray-500 transition-all duration-300 pointer-events-none
                  ${passwordConfirmation || focusedField === 'confirmation' 
                    ? '-top-0.5 text-xs text-blue-600 font-medium' 
                    : 'top-4 text-base text-gray-500'}`}
              >
                Confirmer le mot de passe
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !password || !passwordConfirmation}
              className={`w-full py-3.5 px-6 rounded-xl font-semibold text-white transition-all duration-300 shadow-lg
                flex items-center justify-center gap-2
                ${loading || !password || !passwordConfirmation
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 active:scale-[0.98]'}`}
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-xl" />
                  <span>Mise à jour...</span>
                </>
              ) : (
                <>
                  <span>Réinitialiser le mot de passe</span>
                  <i className="ri-check-line text-lg" />
                </>
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
            >
              <i className="ri-arrow-left-line" />
              Retour à la connexion
            </button>
          </div>
        </div>

        <p className="mt-8 text-center text-sm text-gray-500/80">
          © {new Date().getFullYear()} MAI MAB SERVICES
        </p>
      </div>
    </div>
  );
}